import { createTheme, alpha } from "@mui/material";

// 2026 標準: Geist / shadcn / Linear 風 dark-first
// - 背景は純黒回避 (#0A0A0B), カードは #111113
// - ボーダーのみで影は使わない
// - Inter Variable + JetBrains Mono
// - negative letter-spacing on headings
// - tabular-nums for numbers
// - accent: violet

const ACCENT = "#7C5CFF"; // violet (Linear-like)
const BG = "#0A0A0B";
const PAPER = "#111113";
const BORDER = "rgba(255,255,255,0.08)";
const BORDER_STRONG = "rgba(255,255,255,0.12)";

export const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: ACCENT, contrastText: "#fff" },
    background: { default: BG, paper: PAPER },
    text: {
      primary: "rgba(255,255,255,0.92)",
      secondary: "rgba(255,255,255,0.56)",
      disabled: "rgba(255,255,255,0.32)",
    },
    divider: BORDER,
    success: { main: "#22C55E" },
    error: { main: "#EF4444" },
    warning: { main: "#F59E0B" },
    info: { main: "#3B82F6" },
  },
  typography: {
    fontFamily:
      '"Inter Variable", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    // clamp() で mobile <> desktop の差を滑らかに
    h1: { fontWeight: 600, letterSpacing: "-0.04em", fontSize: "clamp(2rem, 6vw, 3rem)" },
    h2: { fontWeight: 600, letterSpacing: "-0.03em", fontSize: "clamp(1.625rem, 5vw, 2.25rem)" },
    h3: { fontWeight: 600, letterSpacing: "-0.03em", fontSize: "clamp(1.375rem, 4vw, 1.875rem)" },
    h4: { fontWeight: 600, letterSpacing: "-0.02em", fontSize: "clamp(1.25rem, 3.5vw, 1.625rem)" },
    h5: { fontWeight: 600, letterSpacing: "-0.02em", fontSize: "clamp(1.125rem, 3vw, 1.375rem)" },
    h6: { fontWeight: 600, letterSpacing: "-0.01em", fontSize: "clamp(1rem, 2.5vw, 1.125rem)" },
    button: { textTransform: "none", fontWeight: 500, letterSpacing: "-0.01em" },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.55 },
    caption: {
      fontFamily:
        '"JetBrains Mono", "Geist Mono", ui-monospace, "SF Mono", monospace',
      fontVariantNumeric: "tabular-nums",
    },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: BG,
          // 微細なノイズで dark の banding 防止
          backgroundImage: `radial-gradient(circle at 50% -10%, ${alpha(ACCENT, 0.08)} 0%, transparent 60%)`,
          backgroundAttachment: "fixed",
        },
        // monospace 用クラス
        ".mono": {
          fontFamily:
            '"JetBrains Mono", "Geist Mono", ui-monospace, monospace',
          fontVariantNumeric: "tabular-nums",
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: `1px solid ${BORDER}`,
          backgroundColor: PAPER,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: alpha(BG, 0.7),
          backdropFilter: "blur(12px) saturate(180%)",
          borderBottom: `1px solid ${BORDER}`,
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: "8px 16px",
          transition:
            "background-color 150ms cubic-bezier(0.4,0,0.2,1), transform 100ms ease",
          "&:active": { transform: "scale(0.98)" },
        },
        containedPrimary: {
          backgroundColor: "#fff",
          color: "#0A0A0B",
          "&:hover": { backgroundColor: alpha("#fff", 0.9) },
        },
        outlined: {
          borderColor: BORDER_STRONG,
          "&:hover": {
            borderColor: alpha("#fff", 0.2),
            backgroundColor: alpha("#fff", 0.03),
          },
        },
        text: {
          "&:hover": { backgroundColor: alpha("#fff", 0.04) },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: alpha("#fff", 0.02),
          "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_STRONG },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha("#fff", 0.2),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: ACCENT,
            borderWidth: 1,
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.3)}`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily:
            '"JetBrains Mono", "Geist Mono", ui-monospace, monospace',
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.01em",
          borderRadius: 6,
          height: 22,
          fontSize: 11,
          fontWeight: 500,
        },
        outlined: {
          borderColor: BORDER_STRONG,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: 2,
          backgroundColor: alpha("#fff", 0.06),
        },
        bar: { borderRadius: 2 },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: BORDER },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1A1A1D",
          border: `1px solid ${BORDER}`,
          fontSize: 12,
        },
      },
    },
  },
});

export const tokens = {
  bg: BG,
  paper: PAPER,
  border: BORDER,
  borderStrong: BORDER_STRONG,
  accent: ACCENT,
};
